import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../../components/ui/table";
import { Checkbox } from "../../../../../components/ui/checkbox";
import { Label } from "../../../../../components/ui/label";
import { Input } from "../../../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../../components/ui/tooltip";
import { Button } from "../../../../../components/ui/button";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";
import {
  generatedAttributeTypes,
  type ParsedColumn,
  type GeneratedAttributeType,
} from "../../../../../models/audit/sampleUpload";

interface DetectedColumnsTableProps {
  columns: ParsedColumn[];
  matchColumnIndices: Set<number>;
  displayColumnIndices: Set<number>;
  onMatchColumnToggle: (columnIndex: number) => void;
  onDisplayColumnToggle: (columnIndex: number) => void;
  onColumnNameChange: (columnIndex: number, name: string) => void;
  onDetectedTypeChange: (
    columnIndex: number,
    type: GeneratedAttributeType,
  ) => void;
}

export function DetectedColumnsTable({
  columns,
  matchColumnIndices,
  displayColumnIndices,
  onMatchColumnToggle,
  onDisplayColumnToggle,
  onColumnNameChange,
  onDetectedTypeChange,
}: DetectedColumnsTableProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
            Detected Columns
          </h3>
          <p className="text-xs text-stone-400 mt-1">
            Configure how each column should be used
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-xs text-stone-500"
          onClick={() => setShowAdvanced((prev) => !prev)}
        >
          {showAdvanced ? (
            <ChevronDown className="h-3.5 w-3.5 mr-1" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 mr-1" />
          )}
          Advanced
        </Button>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <TooltipProvider>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-52">Column Name</TableHead>
                <TableHead>Sample Values</TableHead>
                {showAdvanced && (
                  <>
                    <TableHead className="w-40">Field Type</TableHead>
                    <TableHead className="w-20 text-center">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help border-b border-dotted border-stone-400">
                            Display
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs">
                            Show this column in the workpaper table
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TableHead>
                    <TableHead className="w-24 text-center">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help border-b border-dotted border-stone-400">
                            Match On
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs">
                            Use this column to match evidence documents to
                            samples
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TableHead>
                  </>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {columns.map((column) => (
                <TableRow key={column.columnIndex}>
                  <TableCell>
                    <Input
                      value={column.columnName}
                      onChange={(e) =>
                        onColumnNameChange(column.columnIndex, e.target.value)
                      }
                      className="h-8 text-sm"
                    />
                  </TableCell>
                  <TableCell className="text-stone-600 text-sm">
                    {column.sampleValues
                      .slice(0, 3)
                      .map((v) => (v === null ? "" : String(v)))
                      .join(", ")}
                    {column.sampleValues.length > 3 && "..."}
                  </TableCell>
                  {showAdvanced && (
                    <>
                      <TableCell>
                        <Select
                          value={column.detectedType}
                          onValueChange={(value: GeneratedAttributeType) =>
                            onDetectedTypeChange(column.columnIndex, value)
                          }
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {generatedAttributeTypes.map((type) => (
                              <SelectItem key={type} value={type}>
                                {formatDetectedType(type)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center">
                          <Checkbox
                            id={`display-column-${column.columnIndex}`}
                            checked={displayColumnIndices.has(
                              column.columnIndex,
                            )}
                            onCheckedChange={() =>
                              onDisplayColumnToggle(column.columnIndex)
                            }
                          />
                          <Label
                            htmlFor={`display-column-${column.columnIndex}`}
                            className="sr-only"
                          >
                            Display {column.columnName} in workpaper
                          </Label>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center">
                          <Checkbox
                            id={`match-column-${column.columnIndex}`}
                            checked={matchColumnIndices.has(column.columnIndex)}
                            onCheckedChange={() =>
                              onMatchColumnToggle(column.columnIndex)
                            }
                          />
                          <Label
                            htmlFor={`match-column-${column.columnIndex}`}
                            className="sr-only"
                          >
                            Use {column.columnName} as match column
                          </Label>
                        </div>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TooltipProvider>
      </div>
    </div>
  );
}

function formatDetectedType(type: string): string {
  const typeMap: Record<string, string> = {
    unique_id: "Unique ID",
    name: "Name",
    amount: "Amount",
    date: "Date",
    generic_string: "Text",
    generic_number: "Number",
    sample_number: "Sample Number",
  };
  return typeMap[type] ?? type;
}
